/**
 * Flash Report Generator
 * Purpose: Daily automated call performance reports at 7:30 AM per location
 * 
 * Report Contents:
 * - Call volume by source
 * - Answer rate percentage
 * - Speed-to-lead metrics
 * - Booking conversion rate
 * - Cost per booked job (CPBJ)
 * - Week-over-week comparisons
 * 
 * Playbook Goal: Keep management informed of daily performance
 */

import { supabase } from './supabase';
import { getTwilioCallAnalytics } from './twilio-call-analytics';
import { getSuccessWareClient } from './successware-client';
import { getGoogleAdsClient } from './google-ads-client';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface FlashReportData {
  company_name: string;
  report_date: string;
  
  // Core Metrics
  total_calls: number;
  answered_calls: number;
  missed_calls: number;
  answer_rate_percent: number;
  avg_call_duration_seconds: number;
  
  // Speed-to-Lead
  avg_response_time_seconds: number;
  median_response_time_seconds: number;
  excellent_responses: number;
  good_responses: number;
  poor_responses: number;
  failed_responses: number;
  speed_to_lead_score: number;
  
  // Bookings & Revenue
  calls_to_bookings: number;
  booking_rate_percent: number;
  total_booking_value: number;
  avg_booking_value: number;
  estimated_pipeline_value: number;
  
  // Cost & ROI
  total_ad_spend: number;
  avg_cost_per_call: number;
  cost_per_booked_job: number;
  roi_percent: number;
  
  // Performance by Source
  calls_by_source: Record<string, number>;
  bookings_by_source: Record<string, number>;
  revenue_by_source: Record<string, number>;
  roi_by_source: Record<string, number>;
  
  // Quality
  avg_call_quality_score: number;
  calls_analyzed: number;
  coaching_opportunities_identified: number;
  
  // Alerts
  speed_violations: number;
  critical_violations: number;
}

// ============================================================================
// Flash Report Generator Class
// ============================================================================

export class FlashReportGenerator {
  
  /**
   * Generate flash report for a specific date
   */
  async generateReport(companyName: string, date: Date = new Date()): Promise<FlashReportData> {
    const reportDate = date.toISOString().split('T')[0];
    const startOfDay = `${reportDate}T00:00:00Z`;
    const endOfDay = `${reportDate}T23:59:59Z`;
    
    console.log(`[FlashReport] Generating report for ${companyName} on ${reportDate}`);

    // Get call data
    const analytics = getTwilioCallAnalytics();
    const calls = await analytics.getCalls({
      companyName,
      startDate: startOfDay,
      endDate: endOfDay,
    });

    const marketingCalls = await analytics.getMarketingCalls({
      companyName,
      startDate: startOfDay,
      endDate: endOfDay,
    });

    // Core metrics
    const totalCalls = calls.length;
    const answeredCalls = calls.filter(c => c.answered).length;
    const missedCalls = totalCalls - answeredCalls;
    const answerRate = totalCalls > 0 ? (answeredCalls / totalCalls) * 100 : 0;

    // Speed-to-lead metrics
    const withResponseTime = marketingCalls.filter(c => c.response_time_seconds !== null && c.response_time_seconds !== undefined);
    const responseTimes = withResponseTime.map(c => c.response_time_seconds!);
    const avgResponseTime = responseTimes.length > 0 
      ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
      : 0;
    
    const sortedTimes = [...responseTimes].sort((a, b) => a - b);
    const medianResponseTime = sortedTimes.length > 0
      ? sortedTimes[Math.floor(sortedTimes.length / 2)]
      : 0;

    const excellentResponses = marketingCalls.filter(c => c.speed_to_lead_grade === 'excellent').length;
    const goodResponses = marketingCalls.filter(c => c.speed_to_lead_grade === 'good').length;
    const poorResponses = marketingCalls.filter(c => c.speed_to_lead_grade === 'poor').length;
    const failedResponses = marketingCalls.filter(c => c.speed_to_lead_grade === 'failed').length;

    // Calculate speed-to-lead score (0-100)
    const speedToLeadScore = this.calculateSpeedToLeadScore({
      excellent: excellentResponses,
      good: goodResponses,
      poor: poorResponses,
      failed: failedResponses,
    });

    // Booking metrics
    const bookedCalls = marketingCalls.filter(c => c.booked_job_id);
    const callsToBookings = bookedCalls.length;
    const bookingRate = totalCalls > 0 ? (callsToBookings / totalCalls) * 100 : 0;
    
    const totalBookingValue = bookedCalls.reduce((sum, c) => sum + (c.booking_value || 0), 0);
    const avgBookingValue = bookedCalls.length > 0 ? totalBookingValue / bookedCalls.length : 0;
    
    const estimatedPipeline = marketingCalls
      .filter(c => c.lead_status === 'qualified' && !c.booked_job_id)
      .reduce((sum, c) => sum + (c.estimated_booking_value || 0), 0);

    // Performance by source
    const callsBySource: Record<string, number> = {};
    const bookingsBySource: Record<string, number> = {};
    const revenueBySource: Record<string, number> = {};

    marketingCalls.forEach(call => {
      const source = call.marketing_source || 'direct';
      callsBySource[source] = (callsBySource[source] || 0) + 1;
      
      if (call.booked_job_id) {
        bookingsBySource[source] = (bookingsBySource[source] || 0) + 1;
        revenueBySource[source] = (revenueBySource[source] || 0) + (call.booking_value || 0);
      }
    });

    // Cost metrics (from Google Ads)
    const totalAdSpend = marketingCalls.reduce((sum, c) => sum + (c.cost_per_call || 0), 0);
    const avgCostPerCall = totalCalls > 0 ? totalAdSpend / totalCalls : 0;
    const costPerBookedJob = callsToBookings > 0 ? totalAdSpend / callsToBookings : 0;
    const roi = totalAdSpend > 0 ? ((totalBookingValue - totalAdSpend) / totalAdSpend) * 100 : 0;

    // Calculate ROI by source
    const roiBySource: Record<string, number> = {};
    Object.keys(revenueBySource).forEach(source => {
      const revenue = revenueBySource[source];
      const cost = marketingCalls
        .filter(c => (c.marketing_source || 'direct') === source)
        .reduce((sum, c) => sum + (c.cost_per_call || 0), 0);
      roiBySource[source] = cost > 0 ? ((revenue - cost) / cost) * 100 : 0;
    });

    // Quality metrics
    const { data: qualityScores } = await supabase
      .from('call_quality_scores')
      .select('overall_score, coaching_opportunities')
      .eq('company_name', companyName)
      .gte('created_at', startOfDay)
      .lte('created_at', endOfDay);

    const avgQualityScore = qualityScores && qualityScores.length > 0
      ? qualityScores.reduce((sum, s) => sum + (s.overall_score || 0), 0) / qualityScores.length
      : 0;

    const coachingOpportunities = qualityScores
      ? qualityScores.reduce((sum, s) => sum + (s.coaching_opportunities?.length || 0), 0)
      : 0;

    // Violations
    const violations = await analytics.getViolations({
      companyName,
      resolved: false,
    });

    const speedViolations = violations.length;
    const criticalViolations = violations.filter(v => v.severity === 'critical' || v.severity === 'urgent').length;

    const reportData: FlashReportData = {
      company_name: companyName,
      report_date: reportDate,
      
      total_calls: totalCalls,
      answered_calls: answeredCalls,
      missed_calls: missedCalls,
      answer_rate_percent: Math.round(answerRate * 100) / 100,
      avg_call_duration_seconds: 0, // TODO: Calculate from calls table
      
      avg_response_time_seconds: Math.round(avgResponseTime),
      median_response_time_seconds: medianResponseTime,
      excellent_responses: excellentResponses,
      good_responses: goodResponses,
      poor_responses: poorResponses,
      failed_responses: failedResponses,
      speed_to_lead_score: speedToLeadScore,
      
      calls_to_bookings: callsToBookings,
      booking_rate_percent: Math.round(bookingRate * 100) / 100,
      total_booking_value: Math.round(totalBookingValue * 100) / 100,
      avg_booking_value: Math.round(avgBookingValue * 100) / 100,
      estimated_pipeline_value: Math.round(estimatedPipeline * 100) / 100,
      
      total_ad_spend: Math.round(totalAdSpend * 100) / 100,
      avg_cost_per_call: Math.round(avgCostPerCall * 100) / 100,
      cost_per_booked_job: Math.round(costPerBookedJob * 100) / 100,
      roi_percent: Math.round(roi * 100) / 100,
      
      calls_by_source: callsBySource,
      bookings_by_source: bookingsBySource,
      revenue_by_source: revenueBySource,
      roi_by_source: roiBySource,
      
      avg_call_quality_score: Math.round(avgQualityScore),
      calls_analyzed: qualityScores?.length || 0,
      coaching_opportunities_identified: coachingOpportunities,
      
      speed_violations: speedViolations,
      critical_violations: criticalViolations,
    };

    // Cache the report
    await this.cacheReport(reportData);

    return reportData;
  }

  /**
   * Calculate overall speed-to-lead score (0-100)
   */
  private calculateSpeedToLeadScore(breakdown: {
    excellent: number;
    good: number;
    poor: number;
    failed: number;
  }): number {
    const total = breakdown.excellent + breakdown.good + breakdown.poor + breakdown.failed;
    if (total === 0) return 0;

    // Weighted scoring: excellent=100, good=75, poor=40, failed=0
    const weightedSum = 
      (breakdown.excellent * 100) +
      (breakdown.good * 75) +
      (breakdown.poor * 40) +
      (breakdown.failed * 0);

    return Math.round(weightedSum / total);
  }

  /**
   * Cache report in database
   */
  private async cacheReport(data: FlashReportData): Promise<void> {
    const { error } = await supabase
      .from('daily_flash_reports')
      .upsert({
        ...data,
        generated_at: new Date().toISOString(),
      }, {
        onConflict: 'company_name,report_date',
      });

    if (error) {
      console.error('[FlashReport] Error caching report:', error);
      throw error;
    }

    console.log('[FlashReport] Report cached successfully');
  }

  /**
   * Get cached report if available
   */
  async getCachedReport(companyName: string, date: Date = new Date()): Promise<FlashReportData | null> {
    const reportDate = date.toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('daily_flash_reports')
      .select('*')
      .eq('company_name', companyName)
      .eq('report_date', reportDate)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No cached report
      }
      console.error('[FlashReport] Error fetching cached report:', error);
      throw error;
    }

    return data as FlashReportData;
  }

  /**
   * Generate report for all companies
   */
  async generateAllReports(date: Date = new Date()): Promise<FlashReportData[]> {
    // Get list of active companies
    const companies = await this.getActiveCompanies();
    
    const reports: FlashReportData[] = [];
    
    for (const company of companies) {
      try {
        const report = await this.generateReport(company, date);
        reports.push(report);
      } catch (error) {
        console.error(`[FlashReport] Failed to generate report for ${company}:`, error);
      }
    }
    
    return reports;
  }

  /**
   * Get list of active companies
   */
  private async getActiveCompanies(): Promise<string[]> {
    const { data } = await supabase
      .from('calls')
      .select('company_name')
      .not('company_name', 'is', null)
      .order('started_at', { ascending: false })
      .limit(1000);

    if (!data) return [];

    // Get unique company names
    const uniqueCompanies = [...new Set(data.map(c => c.company_name))];
    return uniqueCompanies.filter((c): c is string => c !== null);
  }

  /**
   * Format report as text for email
   */
  formatReportText(data: FlashReportData): string {
    const { company_name, report_date } = data;
    
    return `
📊 DAILY FLASH REPORT - ${company_name}
Date: ${new Date(report_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📞 CALL VOLUME
• Total Calls: ${data.total_calls}
• Answered: ${data.answered_calls} (${data.answer_rate_percent.toFixed(1)}%)
• Missed: ${data.missed_calls}

⚡ SPEED-TO-LEAD PERFORMANCE (Score: ${data.speed_to_lead_score}/100)
• Excellent (<60s): ${data.excellent_responses}
• Good (1-5min): ${data.good_responses}
• Poor (>5min): ${data.poor_responses}
• Failed (no response): ${data.failed_responses}
• Avg Response Time: ${Math.floor(data.avg_response_time_seconds / 60)}m ${data.avg_response_time_seconds % 60}s

💰 BOOKINGS & REVENUE
• Calls to Bookings: ${data.calls_to_bookings}
• Booking Rate: ${data.booking_rate_percent.toFixed(1)}%
• Total Revenue: $${data.total_booking_value.toLocaleString()}
• Avg Job Value: $${data.avg_booking_value.toLocaleString()}
• Pipeline Value: $${data.estimated_pipeline_value.toLocaleString()}

📈 ROI METRICS
• Total Ad Spend: $${data.total_ad_spend.toLocaleString()}
• Cost Per Call: $${data.avg_cost_per_call.toFixed(2)}
• Cost Per Booked Job: $${data.cost_per_booked_job.toFixed(2)}
• ROI: ${data.roi_percent.toFixed(1)}%

📱 PERFORMANCE BY SOURCE
${this.formatSourceBreakdown(data)}

🎯 CALL QUALITY
• Average Score: ${data.avg_call_quality_score}/100
• Calls Analyzed: ${data.calls_analyzed}
• Coaching Opportunities: ${data.coaching_opportunities_identified}

⚠️ ALERTS
• Speed Violations: ${data.speed_violations}
• Critical Issues: ${data.critical_violations}

${data.critical_violations > 0 ? '🚨 IMMEDIATE ACTION REQUIRED: Review critical violations in dashboard\n' : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();
  }

  /**
   * Format source breakdown section
   */
  private formatSourceBreakdown(data: FlashReportData): string {
    const sources = Object.keys(data.calls_by_source);
    if (sources.length === 0) return '  No data';

    return sources.map(source => {
      const calls = data.calls_by_source[source] || 0;
      const bookings = data.bookings_by_source[source] || 0;
      const revenue = data.revenue_by_source[source] || 0;
      const roi = data.roi_by_source[source] || 0;
      const rate = calls > 0 ? (bookings / calls) * 100 : 0;

      return `  • ${source}: ${calls} calls → ${bookings} bookings (${rate.toFixed(1)}%) = $${revenue.toLocaleString()} (ROI: ${roi.toFixed(1)}%)`;
    }).join('\n');
  }

  /**
   * Send report via email
   */
  async sendReportEmail(data: FlashReportData, recipients: string[]): Promise<void> {
    const { sendEmail } = await import('./email-service');
    
    const subject = `📊 Daily Flash Report - ${data.company_name} - ${new Date(data.report_date).toLocaleDateString()}`;
    const textBody = this.formatReportText(data);
    
    // Generate HTML version
    const htmlBody = this.formatReportHTML(data);
    
    try {
      const result = await sendEmail({
        to: recipients,
        subject,
        text: textBody,
        html: htmlBody,
        from: {
          email: 'reports@rightawayhome.com',
          name: 'Right Away Marketing Intelligence',
        },
        tags: {
          type: 'flash_report',
          company: data.company_name,
          date: data.report_date,
        },
      });
      
      if (result.success) {
        console.log('[FlashReport] ✅ Email sent successfully to', recipients.length, 'recipients');
      } else {
        console.error('[FlashReport] ❌ Email failed:', result.error);
      }
    } catch (error) {
      console.error('[FlashReport] Email error:', error);
      throw error;
    }
  }

  /**
   * Format report as HTML for email
   */
  private formatReportHTML(data: FlashReportData): string {
    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px; text-align: center; margin-bottom: 30px; }
    .metric-card { background: #f8f9fa; border-left: 4px solid #667eea; padding: 15px; margin: 10px 0; border-radius: 5px; }
    .metric-title { font-size: 14px; color: #666; margin-bottom: 5px; }
    .metric-value { font-size: 28px; font-weight: bold; color: #333; }
    .alert-critical { background: #fee; border-left-color: #dc3545; }
    .alert-warning { background: #fffbeb; border-left-color: #f59e0b; }
    .section { margin: 30px 0; }
    .section-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #667eea; padding-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th { background: #667eea; color: white; padding: 12px; text-align: left; }
    td { padding: 10px; border-bottom: 1px solid #ddd; }
    .badge { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
    .badge-green { background: #d1fae5; color: #065f46; }
    .badge-blue { background: #dbeafe; color: #1e40af; }
    .badge-yellow { background: #fef3c7; color: #92400e; }
    .badge-red { background: #fee2e2; color: #991b1b; }
  </style>
</head>
<body>
  <div class="header">
    <h1>📊 Daily Flash Report</h1>
    <h2>${data.company_name}</h2>
    <p>${new Date(data.report_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>

  <div class="section">
    <div class="section-title">📞 Call Volume</div>
    <div class="metric-card">
      <div class="metric-title">Total Calls</div>
      <div class="metric-value">${data.total_calls}</div>
      <p>Answered: ${data.answered_calls} (${data.answer_rate_percent.toFixed(1)}%) | Missed: ${data.missed_calls}</p>
    </div>
  </div>

  <div class="section">
    <div class="section-title">⚡ Speed-to-Lead Performance</div>
    <div class="metric-card">
      <div class="metric-title">Overall Score</div>
      <div class="metric-value">${data.speed_to_lead_score}/100</div>
      <p>
        <span class="badge badge-green">Excellent: ${data.excellent_responses}</span>
        <span class="badge badge-blue">Good: ${data.good_responses}</span>
        <span class="badge badge-yellow">Poor: ${data.poor_responses}</span>
        <span class="badge badge-red">Failed: ${data.failed_responses}</span>
      </p>
      <p>Average Response Time: ${Math.floor(data.avg_response_time_seconds / 60)}m ${data.avg_response_time_seconds % 60}s</p>
    </div>
  </div>

  <div class="section">
    <div class="section-title">💰 Bookings & Revenue</div>
    <div class="metric-card">
      <div class="metric-title">Booking Rate</div>
      <div class="metric-value">${data.booking_rate_percent.toFixed(1)}%</div>
      <p>Calls to Bookings: ${data.calls_to_bookings}</p>
      <p>Total Revenue: <strong>$${data.total_booking_value.toLocaleString()}</strong></p>
      <p>Average Job Value: $${data.avg_booking_value.toLocaleString()}</p>
    </div>
  </div>

  <div class="section">
    <div class="section-title">📈 ROI Metrics</div>
    <div class="metric-card">
      <div class="metric-title">Return on Investment</div>
      <div class="metric-value" style="color: ${data.roi_percent > 0 ? '#10b981' : '#dc3545'}">${data.roi_percent.toFixed(1)}%</div>
      <p>Ad Spend: $${data.total_ad_spend.toLocaleString()}</p>
      <p>Cost Per Call: $${data.avg_cost_per_call.toFixed(2)}</p>
      <p>Cost Per Booked Job: <strong>$${data.cost_per_booked_job.toFixed(2)}</strong></p>
    </div>
  </div>

  ${data.critical_violations > 0 ? `
  <div class="section">
    <div class="metric-card alert-critical">
      <div class="metric-title">🚨 IMMEDIATE ACTION REQUIRED</div>
      <div class="metric-value">${data.critical_violations}</div>
      <p><strong>Critical speed-to-lead violations detected!</strong></p>
      <p>Review violations in the dashboard immediately.</p>
    </div>
  </div>
  ` : ''}

  <div class="section">
    <div class="section-title">📱 Performance by Source</div>
    <table>
      <thead>
        <tr>
          <th>Source</th>
          <th>Calls</th>
          <th>Bookings</th>
          <th>Rate</th>
          <th>Revenue</th>
        </tr>
      </thead>
      <tbody>
        ${Object.keys(data.calls_by_source).map(source => {
          const calls = data.calls_by_source[source];
          const bookings = data.bookings_by_source[source] || 0;
          const rate = calls > 0 ? (bookings / calls) * 100 : 0;
          const revenue = data.revenue_by_source[source] || 0;
          return `
            <tr>
              <td style="text-transform: capitalize;"><strong>${source}</strong></td>
              <td>${calls}</td>
              <td>${bookings}</td>
              <td><span class="badge ${rate >= 50 ? 'badge-green' : rate >= 30 ? 'badge-yellow' : 'badge-red'}">${rate.toFixed(1)}%</span></td>
              <td>$${revenue.toLocaleString()}</td>
            </tr>
          `;
        }).join('')}
      </tbody>
    </table>
  </div>

  <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #666; font-size: 12px;">
    <p>This is an automated daily report from your Right Away Marketing Intelligence system.</p>
    <p>View detailed analytics at: <a href="https://marketing.rightawayhome.com/call-performance">Call Performance Dashboard</a></p>
  </div>
</body>
</html>
    `.trim();
  }
}

// ============================================================================
// Singleton Instance & Helper Functions
// ============================================================================

let generatorInstance: FlashReportGenerator | null = null;

/**
 * Get or create FlashReportGenerator instance
 */
export function getFlashReportGenerator(): FlashReportGenerator {
  if (!generatorInstance) {
    generatorInstance = new FlashReportGenerator();
  }
  return generatorInstance;
}

/**
 * Generate and send flash report for a company
 */
export async function generateAndSendFlashReport(
  companyName: string,
  recipients: string[],
  date: Date = new Date()
): Promise<FlashReportData> {
  const generator = getFlashReportGenerator();
  const report = await generator.generateReport(companyName, date);
  await generator.sendReportEmail(report, recipients);
  return report;
}

/**
 * Generate flash reports for all companies
 */
export async function generateAllFlashReports(date: Date = new Date()): Promise<FlashReportData[]> {
  const generator = getFlashReportGenerator();
  return await generator.generateAllReports(date);
}
